import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { fetchData } from "../../api/client";
import CepCoverageLeafletMap from "./CepCoverageLeafletMap";

function companyKey(region, slug) {
  return `${region}|${slug}`;
}

function payloadRanges(cepRanges) {
  return (cepRanges || []).map((r, idx) => ({
    id: r.id,
    region_key: r.region_key,
    start_cep: r.start_cep,
    end_cep: r.end_cep,
    target_cod_interno: r.target_cod_interno,
    status: r.status,
    ordem: r.ordem != null ? r.ordem : idx,
  }));
}

function initEnabled(workspaces) {
  const ws = new Set();
  const co = new Set();
  for (const w of workspaces || []) {
    ws.add(w.key);
    for (const c of w.companies || []) co.add(companyKey(w.key, c.slug));
  }
  return { ws, co };
}

export default function CepCoverageMapDialog({
  open,
  onClose,
  companyId,
  cepRanges = [],
  onViewCep,
}) {
  const [source, setSource] = useState("form");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [q, setQ] = useState("");
  const [showEffective, setShowEffective] = useState(true);
  const [showOverlaps, setShowOverlaps] = useState(true);
  const [showUnused, setShowUnused] = useState(false);
  const [showUnattended, setShowUnattended] = useState(true);
  const [onlyOverlaps, setOnlyOverlaps] = useState(false);
  const [enabledWs, setEnabledWs] = useState(() => new Set());
  const [enabledCo, setEnabledCo] = useState(() => new Set());
  const [openWs, setOpenWs] = useState(() => new Set());

  const load = async (src) => {
    setBusy(true);
    setError(null);
    try {
      let data;
      if (src === "prod") {
        if (!companyId) throw new Error("Salve a empresa para ver a cobertura gravada.");
        data = await fetchData(`/companies/${companyId}/cep-coverage`, "GET", null, {
          timeout: 60000,
        });
      } else {
        data = await fetchData(
          "/companies/cep-coverage",
          "POST",
          { cepRanges: payloadRanges(cepRanges) },
          { timeout: 60000 }
        );
      }
      const report = data?.points ? data : data?.data && !Array.isArray(data.data) ? data.data : data;
      setCoverage(report || { points: [], workspaces: [], summary: {} });
      const enabled = initEnabled(report?.workspaces);
      setEnabledWs(enabled.ws);
      setEnabledCo(enabled.co);
      const overlaps = new Set(
        (report?.workspaces || [])
          .filter((w) =>
            (report?.points || []).some(
              (p) => p.overlap && p.winner?.region_key === w.key
            )
          )
          .map((w) => w.key)
      );
      setOpenWs(overlaps.size ? overlaps : new Set((report?.workspaces || []).slice(0, 4).map((w) => w.key)));
    } catch (err) {
      setCoverage(null);
      setError(err?.message || "Falha ao montar o mapa de cobertura.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    setSource("form");
    setQ("");
    setShowEffective(true);
    setShowOverlaps(true);
    setShowUnused(false);
    setShowUnattended(true);
    setOnlyOverlaps(false);
    load("form");
    return undefined;
    // reload when dialog opens with current ranges
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const colorByWs = useMemo(() => {
    const m = {};
    for (const w of coverage?.workspaces || []) m[w.key] = w.color;
    return m;
  }, [coverage]);

  const companyOn = (region, slug) =>
    enabledWs.has(region) && enabledCo.has(companyKey(region, slug));

  const styledPoints = useMemo(() => {
    const list = [];
    const partialByIbge = new Map();
    for (const city of coverage?.unattended || []) {
      if (city.kind !== "partial") continue;
      const ibge = String(city.ibge || "");
      if (ibge) partialByIbge.set(ibge, city);
    }
    for (const p of coverage?.points || []) {
      if (onlyOverlaps && !p.overlap) continue;
      const winner = p.winner;
      const winnerOn = winner && companyOn(winner.region_key, winner.target_cod_interno);
      const unusedOn = (p.claimants || []).some((c) => {
        if (
          winner &&
          c.region_key === winner.region_key &&
          c.target_cod_interno === winner.target_cod_interno
        ) {
          return false;
        }
        return companyOn(c.region_key, c.target_cod_interno);
      });
      let visible = false;
      if (showEffective && winnerOn) visible = true;
      if (showUnused && unusedOn) visible = true;
      if (!visible) continue;
      const hadOverlap = Boolean(p.overlap);
      const fillFrom = winnerOn
        ? winner.region_key
        : (p.claimants || []).find((c) => companyOn(c.region_key, c.target_cod_interno))
            ?.region_key;
      const fill = colorByWs[fillFrom] || "#546e7a";
      const gap = showUnattended ? partialByIbge.get(String(p.ibge || "")) : null;
      list.push({
        ...p,
        fill,
        stroke: showOverlaps && hadOverlap ? "#c62828" : gap ? "#ef6c00" : fill,
        overlap: Boolean(showOverlaps && hadOverlap),
        hadOverlap,
        unattendedPartial: Boolean(gap),
        uncovered: gap?.uncovered || p.uncovered,
      });
    }
    if (showUnattended && !onlyOverlaps) {
      for (const city of coverage?.unattended || []) {
        if (city.kind !== "none") continue;
        if (!Number.isFinite(city.lat) || !Number.isFinite(city.lng)) continue;
        list.push({
          ibge: city.ibge,
          city: city.city,
          uf: city.uf,
          lat: city.lat,
          lng: city.lng,
          start_cep: city.start_cep,
          end_cep: city.end_cep,
          source: "unattended",
          overlap: false,
          split: false,
          winner: null,
          claimants: [],
          sample_cep: city.uncovered?.[0]?.start_cep || city.start_cep,
          unattended: true,
          uncovered: city.uncovered,
          fill: "#c62828",
          stroke: "#c62828",
          hadOverlap: false,
        });
      }
    }
    return list;
  }, [
    coverage,
    enabledWs,
    enabledCo,
    showEffective,
    showUnused,
    showUnattended,
    showOverlaps,
    onlyOverlaps,
    colorByWs,
  ]);

  const qNorm = q.trim().toLowerCase();
  const filteredWorkspaces = useMemo(() => {
    const list = coverage?.workspaces || [];
    if (!qNorm) return list;
    return list
      .map((w) => ({
        ...w,
        companies: (w.companies || []).filter(
          (c) =>
            String(c.slug || "").toLowerCase().includes(qNorm) ||
            String(w.key || "").toLowerCase().includes(qNorm)
        ),
      }))
      .filter(
        (w) =>
          String(w.key || "").toLowerCase().includes(qNorm) || (w.companies || []).length > 0
      );
  }, [coverage, qNorm]);

  const toggleWs = (key, checked) => {
    setEnabledWs((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleCo = (region, slug, checked) => {
    setEnabledCo((prev) => {
      const next = new Set(prev);
      const k = companyKey(region, slug);
      if (checked) next.add(k);
      else next.delete(k);
      return next;
    });
    if (checked) {
      setEnabledWs((prev) => new Set(prev).add(region));
    }
  };

  const setAll = (on) => {
    if (!on) {
      setEnabledWs(new Set());
      setEnabledCo(new Set());
      return;
    }
    const enabled = initEnabled(coverage?.workspaces);
    setEnabledWs(enabled.ws);
    setEnabledCo(enabled.co);
  };

  const summary = coverage?.summary || {};

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pb: 1 }}>
        Cerca virtual no mapa
        <Typography variant="body2" color="text.secondary">
          Cada ponto é um município coberto pelas faixas de CEP. A cor é a marca (workspace); o
          vencedor é o destino que o e-commerce usa de fato.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: "min(78vh, 820px)" }}>
        {busy ? <LinearProgress /> : null}
        <Box sx={{ display: "flex", height: "100%", minHeight: 480 }}>
          <Box
            sx={{
              width: 340,
              flexShrink: 0,
              borderRight: "1px solid",
              borderColor: "divider",
              overflow: "auto",
              p: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Qual versão das faixas mostrar
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant={source === "form" ? "contained" : "outlined"}
                  onClick={() => {
                    setSource("form");
                    load("form");
                  }}
                >
                  Edição desta tela
                </Button>
                <Tooltip
                  title={
                    companyId
                      ? "Faixas já salvas no banco — é o que o e-commerce usa hoje."
                      : "Salve a empresa para comparar com a versão publicada."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant={source === "prod" ? "contained" : "outlined"}
                      disabled={!companyId}
                      onClick={() => {
                        setSource("prod");
                        load("prod");
                      }}
                    >
                      Já salvo (site)
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {source === "prod"
                  ? "Mapa da cerca publicada: o que já está gravado e o e-commerce usa hoje."
                  : "Mapa do rascunho: inclui inclusões, exclusões e importações ainda não salvas."}
              </Typography>
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Typography variant="caption" color="text.secondary">
                {summary.points || 0} ponto(s)
                {summary.source === "municipality"
                  ? ` · ${summary.municipalities || 0} município(s)`
                  : summary.source === "prefix2"
                    ? " · aproximação por faixa de CEP (CSV municipal indisponível)"
                    : ""}
                {summary.overlaps ? ` · ${summary.overlaps} sobreposição(ões)` : ""}
                {summary.splits ? ` · ${summary.splits} cidade(s) fatiada(s)` : ""}
                {summary.invalidGeo
                  ? ` · ${summary.invalidGeo} ainda sem coordenada (IBGE/CEP)`
                  : ""}
                {summary.unattendedNone
                  ? ` · ${summary.unattendedNone} cidade(s) sem cerca`
                  : ""}
                {summary.unattendedPartial
                  ? ` · ${summary.unattendedPartial} com faixa incompleta`
                  : ""}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showEffective}
                    onChange={(e) => setShowEffective(e.target.checked)}
                  />
                }
                label="Cobertura efetiva (vencedor)"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showOverlaps}
                    onChange={(e) => setShowOverlaps(e.target.checked)}
                  />
                }
                label="Sinalizar sobreposições"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showUnused}
                    onChange={(e) => setShowUnused(e.target.checked)}
                  />
                }
                label="Faixas não usadas (perdedoras)"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showUnattended}
                    onChange={(e) => setShowUnattended(e.target.checked)}
                  />
                }
                label="Cidades não atendidas"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={onlyOverlaps}
                    onChange={(e) => setOnlyOverlaps(e.target.checked)}
                  />
                }
                label="Mostrar só conflitos"
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setAll(true)}>
                  Todas
                </Button>
                <Button size="small" onClick={() => setAll(false)}>
                  Nenhuma
                </Button>
              </Stack>
              <TextField
                size="small"
                placeholder="Filtrar marca ou empresa"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <Stack spacing={0.5}>
                {filteredWorkspaces.map((w) => {
                  const expanded = openWs.has(w.key);
                  const companies = w.companies || [];
                  const checkedCount = companies.filter((c) =>
                    companyOn(w.key, c.slug)
                  ).length;
                  return (
                    <Box key={w.key} sx={{ borderRadius: 1, bgcolor: "action.hover", px: 0.5 }}>
                      <Stack direction="row" alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setOpenWs((prev) => {
                              const next = new Set(prev);
                              if (next.has(w.key)) next.delete(w.key);
                              else next.add(w.key);
                              return next;
                            })
                          }
                        >
                          {expanded ? <ExpandMoreRoundedIcon /> : <ChevronRightRoundedIcon />}
                        </IconButton>
                        <Checkbox
                          size="small"
                          checked={enabledWs.has(w.key) && checkedCount === companies.length && companies.length > 0}
                          indeterminate={checkedCount > 0 && checkedCount < companies.length}
                          onChange={(e) => {
                            toggleWs(w.key, e.target.checked);
                            setEnabledCo((prev) => {
                              const next = new Set(prev);
                              for (const c of companies) {
                                const k = companyKey(w.key, c.slug);
                                if (e.target.checked) next.add(k);
                                else next.delete(k);
                              }
                              return next;
                            });
                          }}
                        />
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: w.color,
                            mr: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }} noWrap title={w.key}>
                          {w.key}
                        </Typography>
                        <Chip size="small" label={w.municipalities} sx={{ height: 20 }} />
                      </Stack>
                      <Collapse in={expanded}>
                        <Stack sx={{ pl: 5, pb: 1 }}>
                          {companies.map((c) => (
                            <FormControlLabel
                              key={c.slug}
                              sx={{ ml: 0, mr: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={companyOn(w.key, c.slug)}
                                  onChange={(e) => toggleCo(w.key, c.slug, e.target.checked)}
                                />
                              }
                              label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption">{c.slug || "—"}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {c.municipalities}
                                  </Typography>
                                </Stack>
                              }
                            />
                          ))}
                        </Stack>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
              <Stack spacing={0.5} sx={{ pt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Legenda
                </Typography>
                <Typography variant="caption">Borda vermelha = sobreposição (só um range vale)</Typography>
                <Typography variant="caption">Borda laranja = cidade fatiada entre destinos</Typography>
                <Typography variant="caption">Ponto vermelho oco = cidade sem cerca</Typography>
                <Typography variant="caption">Borda laranja no ponto coberto = faixa incompleta</Typography>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!busy && !error && styledPoints.length === 0 ? (
              <Alert severity="info" sx={{ m: 2 }}>
                Nada para mostrar com os filtros atuais.
              </Alert>
            ) : (
              <CepCoverageLeafletMap points={styledPoints} onViewCep={onViewCep} />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
